import { client as apolloClient } from '@xbcb/apollo-client';
import { executeMutation } from './executeMutation';
import {
  updateWorkOrderStatus,
  updateWorkOrderTaskStatus,
} from './sharedQueries';
import { WorkOrderStatus, WorkOrderTaskStatus } from '@xbcb/work-order-types';
import { message } from 'antd';
import { ShipmentCommercialInvoiceDetailStatus } from '__generated__/graphql';
import { getRecordPath } from './getRecordPath';
import { getRecordType } from '@xbcb/core';
import { RecordType } from '@xbcb/shared-types';
import {
  GET_WORK_ORDER_TASK,
  SEARCH_SHIPMENTS,
  UPDATE_SHCID,
} from './handleRejectTask.query';

interface HandleRejectTaskParams {
  bookingId: string;
  workOrderId: string;
  workOrderVersion: number;
  workOrderTaskId?: string;
  push: (path: string) => void;
}

export const handleRejectTask = async ({
  bookingId,
  workOrderId,
  workOrderVersion,
  workOrderTaskId,
  push,
}: HandleRejectTaskParams): Promise<void> => {
  try {
    const { data: shipmentData } = await apolloClient.query({
      query: SEARCH_SHIPMENTS,
      variables: {
        input: {
          searchCriteria: {
            bookingId: {
              operator: 'EQUALS',
              values: [bookingId],
            },
          },
        },
      },
      fetchPolicy: 'network-only',
    });

    const shcid =
      shipmentData?.searchShipments?.results?.[0]?.legs?.[0]
        ?.commercialInvoiceDetail;

    if (!shcid?.id) {
      throw new Error(
        'ShipmentCommercialInvoiceDetail not found for this booking',
      );
    }

    await executeMutation({
      mutation: UPDATE_SHCID,
      variables: {
        id: shcid.id,
        version: shcid.version,
        input: {
          status: ShipmentCommercialInvoiceDetailStatus.REJECTED,
        },
      },
      successMessage: 'Commercial invoice rejected successfully',
    });

    await executeMutation({
      mutation: updateWorkOrderStatus,
      variables: {
        id: workOrderId,
        version: workOrderVersion,
        input: {
          status: WorkOrderStatus.BLOCKED,
        },
      },
    });

    if (workOrderTaskId) {
      const { data: taskData } = await apolloClient.query({
        query: GET_WORK_ORDER_TASK,
        variables: { id: workOrderTaskId },
        fetchPolicy: 'network-only',
      });
      const latestTaskVersion = taskData?.workOrderTask?.version;

      await executeMutation({
        mutation: updateWorkOrderTaskStatus,
        variables: {
          id: workOrderTaskId,
          version: latestTaskVersion,
          input: {
            status: WorkOrderTaskStatus.BLOCKED,
            tags: [
              { key: 'BLOCKED_REASON', value: 'SELLER_DOCUMENTS_REJECTED' },
            ],
          },
        },
      });
    }

    const recordType = getRecordType(workOrderId) as RecordType;
    const recordPath = getRecordPath({ recordType, id: workOrderId });
    push(`${recordPath}?tab=Support Cases`);
  } catch (error) {
    console.error('Failed to reject task:', error);
    void message.error(
      `Failed to reject task: ${error.message || 'Unknown error'}`,
      5.0,
    );
    throw error;
  }
};
